export const WEEK_DAYS_ORDERED = [1, 2, 3, 4, 5, 6, 0]

export const SCHEDULE_TYPE_CARRIER = 'carrier'
export const SCHEDULE_TYPE_CROSS_CARRIER = 'cross_carrier'
export const SCHEDULE_TYPE_SHIPPING_METHOD = 'shipping_method'

export const detectScheduleType = schedule => {
  if (!schedule) return SCHEDULE_TYPE_CARRIER
  if (schedule.shipping_method_id) return SCHEDULE_TYPE_SHIPPING_METHOD
  if (!schedule.carrier_name && (schedule.channel_type_id || schedule.shipping_state)) return SCHEDULE_TYPE_CROSS_CARRIER
  return SCHEDULE_TYPE_CARRIER
}

export const ACTION_STYLES = {
  create: 'bg-green-100 text-green-800',
  update: 'bg-blue-100 text-blue-800',
  delete: 'bg-red-100 text-red-800',
  costs_create: 'bg-green-100 text-green-800',
  costs_update: 'bg-blue-100 text-blue-800',
  coverages_create: 'bg-green-100 text-green-800',
  coverages_update: 'bg-blue-100 text-blue-800',
  zone_sync: 'bg-indigo-100 text-indigo-800',
}

const LOCALE_MAP = { pt: 'pt-BR', es: 'es' }

export const formatDate = (dateStr, language, timeZone) => {
  if (!dateStr) return '-'
  const date = new Date(dateStr)
  const options = {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  }
  if (timeZone) options.timeZone = timeZone
  return date.toLocaleString(LOCALE_MAP[language] || language, options)
}

export const getChangedFields = (entry, historyFields) => {
  if (entry.action === 'create') {
    return historyFields
      .filter(field => entry.after_change?.[field] != null)
      .map(field => ({
        field,
        oldValue: null,
        newValue: entry.after_change[field],
      }))
  }

  return historyFields
    .filter(field => {
      const before = entry.before_change?.[field]
      const after = entry.after_change?.[field]
      return JSON.stringify(before) !== JSON.stringify(after)
    })
    .map(field => ({
      field,
      oldValue: entry.before_change?.[field],
      newValue: entry.after_change?.[field],
    }))
}

export const formatDispatchTime = timeStr => {
  if (!timeStr) return '-'
  const match = timeStr.match(/(\d{2}):(\d{2})/)
  if (match) return `${match[1]}:${match[2]}`
  return '-'
}

export const durationToMinutes = iso => {
  const match = String(iso).match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/)
  if (!match) return 0
  return parseInt(match[1] || '0', 10) * 60 + parseInt(match[2] || '0', 10)
}

export const calculateCutoffTime = (dispatchTime, durationISO) => {
  if (!dispatchTime || !durationISO) return null
  const timeMatch = dispatchTime.match(/(\d{2}):(\d{2})/)
  if (!timeMatch) return null
  const durMatch = String(durationISO).match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/)
  if (!durMatch) return null
  const dispatchMinutes = parseInt(timeMatch[1], 10) * 60 + parseInt(timeMatch[2], 10)
  const durationMinutes = parseInt(durMatch[1] || '0', 10) * 60 + parseInt(durMatch[2] || '0', 10)
  let cutoffMinutes = dispatchMinutes - durationMinutes
  let daysBefore = 0
  while (cutoffMinutes < 0) {
    cutoffMinutes += 24 * 60
    daysBefore++
  }
  const h = String(Math.floor(cutoffMinutes / 60)).padStart(2, '0')
  const m = String(cutoffMinutes % 60).padStart(2, '0')
  return { time: `${h}:${m}`, daysBefore }
}

/**
 * Computes the cutoff time in total minutes from midnight for a schedule.
 * Accounts for daysBefore by subtracting full days (e.g. daysBefore=1 means cutoff is yesterday).
 * Returns null if cutoff data can't be computed.
 */
export const getCutoffMinutes = (dispatchTime, timeBeforeDispatch) => {
  const cutoffData = calculateCutoffTime(dispatchTime, timeBeforeDispatch)
  if (!cutoffData) return null
  const [h, m] = cutoffData.time.split(':').map(Number)
  // Subtract daysBefore * 24h to get effective minutes relative to today
  return h * 60 + m - cutoffData.daysBefore * 24 * 60
}

export const getTimezoneUtcOffset = timeZone => {
  if (!timeZone) return '+00'
  try {
    const now = new Date()
    const formatter = new Intl.DateTimeFormat('en-US', {
      timeZone,
      timeZoneName: 'shortOffset',
    })
    const parts = formatter.formatToParts(now)
    const tzPart = parts.find(p => p.type === 'timeZoneName')
    if (tzPart) {
      const match = tzPart.value.match(/GMT([+-])(\d{1,2})(?::(\d{2}))?/)
      if (match) {
        const sign = match[1]
        const hours = match[2].padStart(2, '0')
        const minutes = match[3] || '00'
        return minutes === '00' ? `${sign}${hours}` : `${sign}${hours}:${minutes}`
      }
    }
    return '+00'
  } catch {
    return '+00'
  }
}

/**
 * Returns { dayOfWeek, hours, minutes } in the given IANA timezone.
 * Falls back to local time if timezone is invalid or not provided.
 */
export const getTimeInTimezone = (date, timeZone) => {
  try {
    if (!timeZone) throw new Error('no tz')
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone,
      weekday: 'short',
      hour: 'numeric',
      minute: 'numeric',
      hour12: false,
    }).formatToParts(date)
    const get = type => parts.find(p => p.type === type)?.value
    const dayMap = { Sun: 0, Mon: 1, Tue: 2, Wed: 3, Thu: 4, Fri: 5, Sat: 6 }
    return {
      dayOfWeek: dayMap[get('weekday')] ?? date.getDay(),
      hours: parseInt(get('hour'), 10) % 24,
      minutes: parseInt(get('minute'), 10),
    }
  } catch {
    return {
      dayOfWeek: date.getDay(),
      hours: date.getHours(),
      minutes: date.getMinutes(),
    }
  }
}

export const formatDuration = (duration, i18n, tPrefix) => {
  if (!duration) return '-'
  const match = String(duration).match(/^PT(?:(\d+)H)?(?:(\d+)M)?$/)
  if (!match) return duration
  const hours = parseInt(match[1] || '0', 10)
  const minutes = parseInt(match[2] || '0', 10)
  const totalMinutes = hours * 60 + minutes
  const h = Math.floor(totalMinutes / 60)
  const m = totalMinutes % 60
  const parts = []
  if (h > 0) parts.push(i18n.t(`${tPrefix}.duration_units.hours`, { count: h }))
  if (m > 0) parts.push(i18n.t(`${tPrefix}.duration_units.minutes`, { count: m }))
  return parts.join(' ') || '0 min'
}
